import { db } from '../../../db'
import {
  useGetById,
  useGetByIds,
  useGetList,
  useGetPaginatedList,
  useGetQuery,
} from '../../../db/hooks'
import { ListResponse, TABLE_ADDRESSES } from '../../../db/types'
import { updateDatabase } from '../../../shared/utils/syncUtil'
import { Address } from '../types'

const generateIndexedSearch = (address: Address): Array<string> => {
  if (address.search === undefined || address.search === null) {
    return []
  }

  return address.search.toLocaleLowerCase().split(' ')
}

const generateIndexedSort = (address: Address) => {
  return address.sort ?? ''
}

const overrideAddresses = async (addresses: Array<Address>): Promise<void> => {
  const searchableAddresses = addresses.map((address: Address) => {
    return {
      ...address,
      searchIndexed: generateIndexedSearch(address),
      sortIndexed: generateIndexedSort(address),
    }
  })

  await updateDatabase(db, TABLE_ADDRESSES, searchableAddresses)
}

const overrideAddress = async (address: Address): Promise<void> => {
  await db.table(TABLE_ADDRESSES).put({
    ...address,
    searchIndexed: generateIndexedSearch(address),
    sortIndexed: generateIndexedSort(address),
  })
}

const addAddress = async (address: Address): Promise<void> => {
  await db.table(TABLE_ADDRESSES).put({
    ...address,
    searchIndexed: generateIndexedSearch(address),
    sortIndexed: generateIndexedSort(address),
  })
}

const clearAddresses = async (): Promise<void> => {
  await db.table(TABLE_ADDRESSES).clear()
}

const useGetAddresses = (
  search: string,
  page: number,
  size: number,
  filter?: (item: Address) => boolean
) =>
  useGetPaginatedList<Address>(TABLE_ADDRESSES, search, page, size, {
    filter: filter,
    sortBy: 'sortIndexed',
    sortDirection: 'asc',
  })

const useGetFavoriteAddresses = (
  addressTypeIds: string[]
): ListResponse<Address> => {
  return useGetQuery<Address[]>(() => {
    return db
      .table<Address>(TABLE_ADDRESSES)
      .filter(
        (address: Address) =>
          address.favorite &&
          (addressTypeIds.length === 0 ||
            addressTypeIds.includes(address.addressTypeId))
      )
      .sortBy('sortIndexed')
  }, [addressTypeIds])
}

const useGetAddressById = (id: string) => {
  return useGetById<Address>(TABLE_ADDRESSES, id)
}

const getAddressById = (id: string, filter?: (address: Address) => boolean) => {
  return db
    .table(TABLE_ADDRESSES)
    .filter((address: Address) => {
      if (filter && !filter(address)) {
        return false
      }

      return address.id === id
    })
    .first()
}

const useGetAddressesByIds = (ids: string[]) => {
  return useGetByIds<Address>(TABLE_ADDRESSES, ids)
}

const useGetAllAddresses = () => {
  return useGetList<Address>(TABLE_ADDRESSES)
}

const useGetRelatedAddresses = (
  address: Address | null
): ListResponse<Address> => {
  return useGetQuery<Address[] | null>(() => {
    if (!address) {
      return Promise.resolve(null)
    }

    if (address.parentId !== null) {
      return db
        .table<Address>(TABLE_ADDRESSES)
        .where('id')
        .equalsIgnoreCase(address.parentId)
        .sortBy('sortIndexed')
    }

    return db
      .table<Address>(TABLE_ADDRESSES)
      .where('parentId')
      .equalsIgnoreCase(address.id)
      .sortBy('sortIndexed')
  }, [address])
}

export {
  addAddress,
  clearAddresses,
  getAddressById,
  overrideAddress,
  overrideAddresses,
  useGetAddressById,
  useGetAddresses,
  useGetAddressesByIds,
  useGetAllAddresses,
  useGetFavoriteAddresses,
  useGetRelatedAddresses,
}
